import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { OrdersTable } from "../../../components/dashboard/orders/orders-table";
import { ModuleFilter } from "../../../components/utils/ModuleFilter";
import { getOrdersAsync } from "../../../slices/order";
import DashboardHeader from "../../../components/dashboard/dashboardHeader";
import {
  ArrowPathRoundedSquareIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";
import { toast } from "react-hot-toast";
import { mapErrors, updateUrlWithException } from "../../../components/Utils";
import { syncModule } from "../../../api/company";
import SimplePrompt from "../../../components/utils/simplePrompt";
import { debounce } from "lodash";
import {
  orderStatusOptions,
} from "../../../components/dashboard/orders/utils";
import { useSearchParams } from "react-router-dom";
import { format, startOfMonth, endOfMonth } from "date-fns";
import { PeriodSelector } from "../../../components/dashboard/hours/period_selector";

const toISODate = (date) => format(date, "yyyy-MM-dd");

const parseFilterValue = (filter) => {
  if (!filter) return [];
  try {
    const parsed = JSON.parse(filter.value);
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
};

export const OrdersList = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [searchParams] = useSearchParams();

  const defaultPeriod = useMemo(() => {
    const today = new Date();
    return {
      periodType: "month",
      from: toISODate(startOfMonth(today)),
      to: toISODate(endOfMonth(today)),
    };
  }, []);
  const initialOpenOrders = searchParams.get("view") !== "all";

  const [controller, setController] = useState({
    filters: initialOpenOrders
      ? [
          {
            name: "view",
            value: JSON.stringify(["active"]),
          },
        ]
      : [],
    page: searchParams.get("page") ? searchParams.get("page") : 1,
    per_page: searchParams.get("per_page") ? searchParams.get("per_page") : 50,
    query: searchParams.get("query") ? searchParams.get("query") : "",
    sort: searchParams.get("sort") ? searchParams.get("sort") : "DESC",
    sortBy: searchParams.get("sortBy")
      ? searchParams.get("sortBy")
      : "order_number",
    view: initialOpenOrders ? "active" : "all",
    period_type: searchParams.get("period_type") || defaultPeriod.periodType,
    from_date: searchParams.get("from_date") || defaultPeriod.from,
    to_date: searchParams.get("to_date") || defaultPeriod.to,
    fields: "id,order_number,order_date,customer,project,status,total_amount",
  });

  const itemState = useSelector((store) => {
    return store.orders;
  });

  // Update all params from controller except filters (dont't touch filters)
  useEffect(() => {
    updateUrlWithException(
      // These will be updated
      {
        page: controller.page,
        per_page: controller.per_page,
        query: controller.query,
        sort: controller.sort,
        sortBy: controller.sortBy,
        view: controller.view,
        period_type: controller.period_type,
        from_date: controller.from_date,
        to_date: controller.to_date,
      },
      // This will be preserved
      "filters"
    );
  }, [controller]);

  const { isLoading } = itemState;
  const selectedItems = [];

  const handlePageChange = (newPage) => {
    setController({
      ...controller,
      page: newPage,
    });
  };

  const buildInitialModuleFilters = () => {
    const viewFilter = controller.filters.find((filter) => filter.name === "view");
    const statusFilter = controller.filters.find((filter) => filter.name === "status");
    const selectedViews = parseFilterValue(viewFilter);

    return {
      query: controller.query || "",
      active: selectedViews.includes("active"),
      statuses: parseFilterValue(statusFilter),
    };
  };

  const isOpenOrdersSelected = buildInitialModuleFilters().active;

  const handleModuleFilterChange = (filters) => {
    const nextFilters = [];
    const openOrders = filters.active === true;

    if (openOrders) {
      nextFilters.push({
        name: "view",
        value: JSON.stringify(["active"]),
      });
    }

    if (filters.statuses && filters.statuses.length > 0) {
      nextFilters.push({
        name: "status",
        value: JSON.stringify(filters.statuses),
      });
    }

    setController((prev) => ({
      ...prev,
      page: 1,
      query: filters.query || "",
      view: openOrders ? "active" : "all",
      filters: nextFilters,
    }));
  };

  const handlePeriodChange = (period) => {
    setController((prev) => ({
      ...prev,
      page: 1,
      period_type: period.periodType,
      from_date: period.from,
      to_date: period.to,
    }));
  };

  const debouncedDispatchAction = useMemo(
    () =>
      debounce((nextController) => {
        const payload = { ...nextController };
        const viewFilter = payload.filters.find((filter) => filter.name === "view");
        const selectedViews = parseFilterValue(viewFilter);
        if (selectedViews.includes("active")) {
          delete payload.from_date;
          delete payload.to_date;
          delete payload.period_type;
        }
        dispatch(getOrdersAsync(payload));
      }, 250),
    [dispatch]
  );

  useEffect(() => {
    // Trigger the debounced dispatch action
    debouncedDispatchAction(controller);

    // Clean the debounced function on component unmount.
    return () => {
      debouncedDispatchAction.cancel();
    };
  }, [controller, debouncedDispatchAction]);

  const [, setSyncState] = useState({
    isLoading: false,
    error: false,
    errors: [],
    updated: 0,
    created: 0,
    found: 0,
  });

  const runOrderSync = () => {
    return syncModule({ orders: true })
      .then((res) => {
        const result = res.data.orders || {};
        setSyncState((prevState) => ({
          ...prevState,
          isLoading: false,
          updated: result.updated || 0,
          created: result.created || 0,
          found: result.found || 0,
        }));
        toast.success(
          `${result.updated || 0} was updated out of ${result.found || 0} - ${result.created || 0} was created`
        );
        return res;
      })
      .catch((err) => {
        // Updating ItemState
        setSyncState((prevState) => ({
          ...prevState,
          isLoading: false,
          updated: 0,
          created: 0,
          found: 0,
        }));
        return err;
      });
  };

  const SyncOrders = () => {
    setSyncState({
      isLoading: true,
      error: false,
      errors: [],
      updated: 0,
      created: 0,
      found: 0,
    });

    toast.promise(runOrderSync(), {
      loading: t("Syncing...."),
      success: (res) => {
        if (res.data.errors) throw new Error(mapErrors(res.data.errors));
        if (res.status === 500) throw new Error("Server error");
        return `Sync was successfull`;
      },
      error: t("Something went wrong. Could not save data!"),
    });
  };

  const [syncPromptState, setSyncPromptState] = useState(false);

  const headerActions = [
    {
      component: null,
      label: "Sync API",
      description: "",
      path: null,
      action: () => setSyncPromptState(true),
      icon: ArrowPathRoundedSquareIcon,
      textColor: "text-teal-700",
      bgColor: "bg-white",
      hidden: false,
      menu: true,
    },
  ];

  const statusFilterOptions = orderStatusOptions.map((option) => ({
    value: option.value,
    label: t(option.label),
    class: "bg-slate-100 text-slate-800",
  }));

  return (
    <>
      <DashboardHeader
        subtitle=""
        actions={headerActions}
        title={"Orders"}
        meta={[]}
        mainButton={{
          label: "New",
          action: null,
          path: "/dashboard/orders/new",
          color: "",
          icon: PlusIcon,
        }}
      >
        <div className="mx-auto mt-8 grid max-w-3xl grid-cols-1 gap-6 sm:px-6 lg:max-w-7xl lg:grid-flow-col-dense lg:grid-cols-3">
          <div className="space-y-6 lg:col-span-3 lg:col-start-1 sm:col-span-3">
            <ModuleFilter
              className={"mt-10"}
              disabled={isLoading}
              onFiltersChange={handleModuleFilterChange}
              initialFilters={buildInitialModuleFilters()}
              searchPlaceholder={t("Search orders...")}
              hideStatusFilter={true}
              selectedItems={selectedItems}
              customCheckboxButtonFilters={[
                {
                  key: "active",
                  label: t("Åpne ordre"),
                  defaultValue: true,
                },
              ]}
              customDropdownFilters={[
                {
                  key: "statuses",
                  label: t("Status"),
                  options: statusFilterOptions,
                },
              ]}
            />

            {!isOpenOrdersSelected && (
              <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
                <PeriodSelector
                  periodType={controller.period_type}
                  from={controller.from_date}
                  to={controller.to_date}
                  onChange={handlePeriodChange}
                  allowedPeriodTypes={[
                    "day",
                    "week",
                    "month",
                    "quarter",
                    "year",
                    "custom",
                  ]}
                />
              </div>
            )}

            <OrdersTable
              itemState={itemState}
              itemsCount={itemState.totalCount}
              currentCount={itemState.currentCount}
              page={controller.page}
              onPageChange={handlePageChange}
              perPage={controller.per_page}
            />
          </div>
        </div>
      </DashboardHeader>
      <SimplePrompt
        title="Sync av ordre"
        description="Er du sikker på at du ønsker å synke ordrer nå?"
        onAccept={() => SyncOrders()}
        open={syncPromptState}
        onClose={() => setSyncPromptState(false)}
      />
    </>
  );
};
